import { Autocomplete, TextField } from '@mui/material';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type { GgaFreeSpinGameOption } from '../../@types/casino';
import type { CashbackBonusFormValues } from '../../schema';
import CashbackFreeSpinBetField from './CashbackFreeSpinBetField';
import CashbackTierFields, { type CashbackTierProps } from './CashbackTierFields';

export type CashbackFreeSpinProviderOption = {
  id: string;
  name: string;
};

type Props = CashbackTierProps & {
  games: GgaFreeSpinGameOption[];
  providers: CashbackFreeSpinProviderOption[];
};

const CashbackFreeSpinTierRow = ({ disabled, games, index, onRemove, providers, showRemove }: Props) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<CashbackBonusFormValues>();
  const [providerId, gameId] = useWatch({
    control,
    name: [`freeSpinTiers.${index}.providerId`, `freeSpinTiers.${index}.gameId`],
  });
  const providerGames = useMemo(
    () => games.filter((game) => String(game.providerId) === providerId),
    [games, providerId]
  );
  const selectedGame = providerGames.find((game) => game.id === gameId);
  const isLevelGame = selectedGame?.freeSpinType?.trim().toLowerCase() === 'level';
  const tierErrors = errors.freeSpinTiers?.[index];

  const clearSelectedGame = () => {
    setValue(`freeSpinTiers.${index}.gameId`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`freeSpinTiers.${index}.gameName`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`freeSpinTiers.${index}.betValuePerSpin`, null, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <CashbackTierFields index={index} disabled={disabled} onRemove={onRemove} showRemove={showRemove}>
      <Controller
        name={`freeSpinTiers.${index}.providerId`}
        control={control}
        render={({ field, fieldState }) => {
          const selectedProvider = providers.find((provider) => provider.id === field.value) ?? null;

          return (
            <Autocomplete
              options={providers}
              value={selectedProvider}
              onChange={(_event, provider) => {
                setValue(`freeSpinTiers.${index}.providerId`, provider?.id ?? '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue(`freeSpinTiers.${index}.providerName`, provider?.name ?? '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                clearSelectedGame();
              }}
              onBlur={field.onBlur}
              getOptionLabel={(provider) => provider.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Provider'
                  required
                  error={!!fieldState.error || !!tierErrors?.providerId}
                  helperText={fieldState.error?.message ?? tierErrors?.providerId?.message}
                />
              )}
            />
          );
        }}
      />
      <Controller
        name={`freeSpinTiers.${index}.gameId`}
        control={control}
        render={({ field, fieldState }) => {
          const currentGame = providerGames.find((game) => game.id === field.value) ?? null;

          return (
            <Autocomplete
              options={providerGames}
              value={currentGame}
              onChange={(_event, game) => {
                setValue(`freeSpinTiers.${index}.gameId`, game?.id ?? '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue(`freeSpinTiers.${index}.gameName`, game?.name ?? '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue(`freeSpinTiers.${index}.betValuePerSpin`, null, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              onBlur={field.onBlur}
              getOptionLabel={(game) => game.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={disabled || !providerId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Game'
                  required
                  error={!!fieldState.error || !!tierErrors?.gameId}
                  helperText={fieldState.error?.message ?? tierErrors?.gameId?.message}
                />
              )}
            />
          );
        }}
      />
      <CashbackFreeSpinBetField disabled={disabled} gameId={gameId} index={index} isLevelGame={isLevelGame} />
    </CashbackTierFields>
  );
};

export default CashbackFreeSpinTierRow;
